import type { PlanTier } from './auth';

// ─── Centralized AI Credit Costs ─────────────────────────────────────────────
// Single source of truth for all credit deductions across the platform.
// Update costs here — all API and dashboard code references this file.

export const AI_CREDIT_COSTS = {
  // Menu operations
  MENU_IMPORT:          10,   // Full menu extraction from PDF/image/URL
  ADDON_CREATION:        3,   // Addon group/option extraction

  // Content generation
  DESCRIPTION:           1,   // AI copywriting for a menu item
  TRANSLATION:           1,   // Per-item translation

  // Image & video
  IMAGE_GENERATION:      2,   // Studio-quality food photo
  VIDEO_8S:             15,   // 8-second social video
  VIDEO_16S:            25,   // 16-second social video

  // Inference
  DIETARY_INFERENCE:     1,   // Infer dietary tags
  CALORIE_INFERENCE:     1,   // Infer calorie count

  // AI Order Assistant — charged once per conversation session (first message only)
  ORDER_ASSISTANT:       1,   // 1 credit per conversation session

  // General LLM invocation
  LLM_INVOKE:            2,   // Generic LLM call
} as const;

export type AiActionType = keyof typeof AI_CREDIT_COSTS;

// Human-readable labels for the activity history
export const AI_ACTION_LABELS: Record<string, string> = {
  MENU_IMPORT:       'Menu Import',
  ADDON_CREATION:    'Addon Creation',
  DESCRIPTION:       'AI Description',
  TRANSLATION:       'Translation',
  IMAGE_GENERATION:  'AI Photography',
  VIDEO_8S:          'Video (8s)',
  VIDEO_16S:         'Video (16s)',
  DIETARY_INFERENCE: 'Dietary Inference',
  CALORIE_INFERENCE: 'Calorie Inference',
  ORDER_ASSISTANT:   'AI Order Assistant',
  LLM_INVOKE:        'AI Processing',
};

// ─── Plan-based monthly credit limits ────────────────────────────────────────

/**
 * Keyed by `PlanTier`, so the COMPILER refuses a tier that is missing.
 *
 * It was `Record<string, number>` and had no `hotel`, and `getMonthlyCredits` ends in
 * `?? 0` — so a Hotel venue was handed ZERO AI credits a month. Everything credit-metered
 * was therefore off for them: menu import, description and image generation, menu
 * translation, and the guest AI assistant (which is gated on
 * `getMonthlyCredits(plan) > 0`). Nothing reported an error; the features simply were
 * not there.
 *
 * Hotel shares Scale's rung, so it gets Scale's allowance.
 */
export const PLAN_MONTHLY_CREDITS: Record<PlanTier, number> = {
  free:       0,
  starter:    0,
  menu:       1_000,
  control:    1_000,     // legacy alias for menu
  operate:    2_000,
  scale:      4_000,
  hotel:      4_000,     // Scale's rung, Scale's credits
  command:    10_000,
  enterprise: 10_000,    // legacy alias for command
};

export function getMonthlyCredits(plan: PlanTier): number {
  return PLAN_MONTHLY_CREDITS[plan] ?? 0;
}

// Low credit threshold — trigger email alert when balance drops below this
export const LOW_CREDIT_THRESHOLD = 100;

// ─── Model pricing (real Gemini API cost, USD) ───────────────────────────────
// Single source of truth for converting token usage → actual dollar cost in the
// per-request cost ledger (ai_request_logs). This is the REAL provider cost, NOT
// the internal credit system above.
//
// ⚠️ PRICES MUST BE RE-VERIFIED against ai.google.dev/gemini-api/docs/pricing
// before they are ever used for billing/quoting. Figures below are user-verified
// (June 2026) and keyed by the exact model-id strings used in apps/api ai.repository.ts.
// Token prices are USD per 1,000,000 tokens; perImage is USD per output image;
// perSecond is USD per output second (video).

export interface ModelPrice {
  inputPerMTok?: number;        // USD / 1M input (prompt) tokens
  outputPerMTok?: number;       // USD / 1M output (candidate) tokens
  cachedInputPerMTok?: number;  // USD / 1M cached-input tokens (context-cache hit)
  perImage?: number;            // USD per generated image
  perSecond?: number;           // USD per generated second (video)
}

export const MODEL_PRICING: Record<string, ModelPrice> = {
  'gemini-2.5-flash':       { inputPerMTok: 0.30, outputPerMTok: 2.50, cachedInputPerMTok: 0.03 },
  'gemini-2.5-flash-lite':  { inputPerMTok: 0.10, outputPerMTok: 0.40, cachedInputPerMTok: 0.01 },
  // Image models — prompt text billed per-token, output billed per-image
  'gemini-2.5-flash-image':                  { inputPerMTok: 0.30, perImage: 0.039 },
  'models/gemini-3.1-flash-image-preview':   { inputPerMTok: 0.50, perImage: 0.067 },
  // Video (Veo 2) — re-verify; perSecond approximate
  'models/veo-2.0-generate-001':             { perSecond: 0.40 },
  // Embeddings (for any future RAG/dedup work)
  'gemini-embedding':       { inputPerMTok: 0.15 },
};

// Token counts as returned by the Gemini SDK on response.usageMetadata.
export interface AiUsageMetadata {
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  totalTokenCount?: number;
  cachedContentTokenCount?: number;
}

/**
 * Estimate the real USD cost of a single model request from its token usage.
 * Cached-input tokens are billed at the cheaper cached rate and subtracted from
 * the uncached input. Returns 0 for unknown models (logged but not priced).
 */
export function estimateRequestCostUsd(
  modelId: string,
  usage?: AiUsageMetadata | null,
  opts?: { images?: number; seconds?: number },
): number {
  const p = MODEL_PRICING[modelId];
  if (!p) return 0;

  const promptTokens = usage?.promptTokenCount ?? 0;
  const cachedTokens = usage?.cachedContentTokenCount ?? 0;
  const outputTokens = usage?.candidatesTokenCount ?? 0;
  const uncachedInput = Math.max(0, promptTokens - cachedTokens);

  let cost = 0;
  if (p.inputPerMTok) cost += (uncachedInput / 1_000_000) * p.inputPerMTok;
  if (p.cachedInputPerMTok) cost += (cachedTokens / 1_000_000) * p.cachedInputPerMTok;
  if (p.outputPerMTok) cost += (outputTokens / 1_000_000) * p.outputPerMTok;
  if (p.perImage && opts?.images) cost += p.perImage * opts.images;
  if (p.perSecond && opts?.seconds) cost += p.perSecond * opts.seconds;
  return cost;
}
